import * as React from "react";
import { Alert, AlertColor, Box } from "@mui/material";
import CclInfoTable, {
    CclInfoTableRowTemplate,
} from "../../../../components/common/cclInfoTable/cclInfoTable";
import { CclInfoTblUserGeneralInfoTemplate } from "./CclInfoTblUserGeneralInfoTemplate";
import { GrandCentralAccountDetail } from "../../../../services/types/cclGrandCentralApiTypes";
import { EnterpriseUserDetail } from "../../../../services/types/userPreferenceTypes";
import CclGenericAccordian from "../../../../components/common/cclGenericAccordian";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import EditUserDrawer from "../../../../components/shared/editUserDrawer/editUserDrawer";
import { UserClaimsService } from "../../../../services/currentUserService/currentUserService";
import { AccessEventIndividualDetails } from "../../../../services/types/accessEventTypes";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { useGetAssessmentAdminGroupDetailByImKeyQuery } from "../../../../services/cclTokenedAssessmentApi";
import { useGetPreferredLanguageQuery } from "../../../../services/cclTokenedEnterpriseUserApi";

export type UserDetailsCardProps = {
    gcAccountEmail: string;
    imKey: number;
    gcDetail: GrandCentralAccountDetail | null;
    enterpriseDetails: EnterpriseUserDetail[] | null;
    clientAdmin?: boolean;
};

type GeneralTableRecord = {
    imKey: number;
    firstName: string;
    lastName: string;
    email: string;
    ceContactId: string;
    accountId: string;
    analyticsId: string;
    assessmentAdminGroup: string;
    administratorGroupType: string;
    preferredLanguage: string;
};

const UserDetailsCard: React.FC<UserDetailsCardProps> = (props) => {
    const [userTemplate, setUserTemplate] = React.useState<CclInfoTableRowTemplate[]>(
        CclInfoTblUserGeneralInfoTemplate,
    );
    const [editDrawerOpen, setEditDrawerOpen] = React.useState<boolean>(false);
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("info");
    const [localFirstName, setLocalFirstName] = React.useState<string>("");
    const [localLastName, setLocalLastName] = React.useState<string>("");
    const { logEvent } = useLogAccessEvent();

    const claimsService = new UserClaimsService();
    const isSuperAdmin = claimsService.IsUserSuperAdmin();

    const { data: assessmentAdminData, isLoading: assessmentAdminLoading } =
        useGetAssessmentAdminGroupDetailByImKeyQuery(props.imKey, {
            skip: !props.imKey,
        });

    const { data: preferredLanguageData, isLoading: preferredLanguageLoading } =
        useGetPreferredLanguageQuery(props.imKey, {
            skip: !props.imKey,
        });

    const mapGroupType = (groupType: string): string => {
        if (groupType === "1") return "Individual";
        if (groupType === "2") return "Shared";
        return groupType;
    };

    const relatedImKeys =
        props.enterpriseDetails?.filter((ed) => ed.imKey !== props.imKey).map((ed) => ed.imKey) ??
        [];
    const enterpriseDetail = props.enterpriseDetails?.find(
        (e) => e.email.toLowerCase() === props.gcAccountEmail.toLowerCase(),
    );
    const genData: GeneralTableRecord = {
        imKey: enterpriseDetail?.imKey ?? 0,
        firstName: (localFirstName || enterpriseDetail?.firstName) ?? "",
        lastName: (localLastName || enterpriseDetail?.lastName) ?? "",
        email: enterpriseDetail?.email ?? "",
        ceContactId: enterpriseDetail?.ceContactId ?? "",
        accountId: props.gcDetail?.accountId ?? "No Account",
        analyticsId: props.gcDetail?.analyticsId ?? "",
        assessmentAdminGroup: assessmentAdminLoading
            ? "Retrieving..."
            : (assessmentAdminData?.aagName ?? "N/A"),
        administratorGroupType: assessmentAdminLoading
            ? "Retrieving..."
            : assessmentAdminData
              ? mapGroupType(assessmentAdminData.groupType)
              : "N/A",
        preferredLanguage: preferredLanguageLoading
            ? "Retrieving..."
            : (preferredLanguageData?.sLanguage ?? "No Preferred Language Set"),
    };

    React.useEffect(() => {
        if (!enterpriseDetail) {
            return;
        }
        setLocalFirstName(enterpriseDetail.firstName);
        setLocalLastName(enterpriseDetail.lastName);
        let tmplt = [...userTemplate];
        if (props.clientAdmin) {
            tmplt = tmplt.filter(
                (r) => r.id !== "accountId" && r.id !== "imKey" && r.id !== "analyticsId",
            );
        }
        let row = tmplt.find((r) => r.id === "fullname");
        if (row != null) {
            row.valueField.value = enterpriseDetail.firstName + " " + enterpriseDetail.lastName;
            row.value = enterpriseDetail.firstName + " " + enterpriseDetail.lastName;
        }
        setUserTemplate(tmplt);
        // eslint-disable-next-line
    }, [enterpriseDetail]);

    const handleEditClose = (success: boolean, newFirstName?: string, newLastName?: string) => {
        setEditDrawerOpen(false);
        if (success) {
            const evtData: AccessEventIndividualDetails = {
                imKey: props.imKey.toString(),
                email: props.gcAccountEmail,
            };
            logEvent("UserDetailsEdited", evtData);

            if (newFirstName !== undefined && newLastName !== undefined) {
                setLocalFirstName(newFirstName);
                setLocalLastName(newLastName);
                const fullName = `${newFirstName} ${newLastName}`;
                setUserTemplate((prev) => {
                    const updated = [...prev];
                    const row = updated.find((r) => r.id === "fullname");
                    if (row != null) {
                        row.valueField.value = fullName;
                        row.value = fullName;
                    }
                    return updated;
                });
                setSnackbarMessage("User Updated");
                setSnackbarSeverity("success");
                setShowSnackbar(true);
            }
        }
    };

    const editButton = isSuperAdmin ? (
        <Box width={1} sx={{ marginRight: 4 }}>
            <CclButton
                mode="primary"
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    setEditDrawerOpen(true);
                }}
                sx={{ float: "right" }}
            >
                Edit
            </CclButton>
        </Box>
    ) : undefined;

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {showSnackbar && (
                <CclAlertSnackbar
                    open={true}
                    onClose={() => setShowSnackbar(false)}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                />
            )}
            <CclGenericAccordian
                headingText={"User"}
                sx={{ height: 1 }}
                summaryRightSideElement={editButton}
            >
                {relatedImKeys != null && relatedImKeys.length > 0 && (
                    <Alert severity="error">
                        There are other IDs associated with {props.gcAccountEmail}:{" "}
                        {relatedImKeys.join(", ")}
                    </Alert>
                )}
                <CclInfoTable template={userTemplate} data={genData} />
            </CclGenericAccordian>
            {isSuperAdmin && (
                <EditUserDrawer
                    title="Edit User"
                    open={editDrawerOpen}
                    imKey={genData.imKey}
                    email={genData.email}
                    firstName={genData.firstName}
                    lastName={genData.lastName}
                    onClose={handleEditClose}
                />
            )}
        </Box>
    );
};

export default UserDetailsCard;
